import { useState, useCallback } from 'react'
import { db } from '@/services/index.ts'

export interface VaultMetadata {
  id: string
  vault_id: number
  benefactor_address: string
  /**
   * Wallet address of the beneficiary (migration 010). Kept so party-based RLS
   * can return later; permissive policies in migration 012 allow app sessions
   * without JWT `wallet_address` to read/write metadata.
   */
  beneficiary_address: string | null
  beneficiary_email: string
  beneficiary_phone: string | null
  vault_type: 'text' | 'file'
  vault_name: string | null
  original_filename: string | null
  created_at: string
}

export interface SaveVaultMetadataParams {
  vaultId: number
  benefactorAddress: string
  /** Required so the row matches the new beneficiary RLS policy. */
  beneficiaryAddress: string
  beneficiaryEmail: string
  beneficiaryPhone?: string
  vaultType: 'text' | 'file'
  vaultName?: string
  originalFilename?: string
}

interface UseVaultMetadataReturn {
  loading: boolean
  error: string | null
  save: (params: SaveVaultMetadataParams) => Promise<VaultMetadata | null>
  fetchByVaultId: (vaultId: number) => Promise<VaultMetadata | null>
  fetchByBenefactor: (address: string) => Promise<VaultMetadata[]>
}

export function useVaultMetadata(): UseVaultMetadataReturn {
  const [loading, setLoadingState] = useState(false)
  const [error, setErrorState] = useState<string | null>(null)

  const setLoading = () => {
    setLoadingState(true)
    setErrorState(null)
  }
  const setError = (e: string) => {
    setLoadingState(false)
    setErrorState(e)
  }
  const setDone = () => {
    setLoadingState(false)
  }

  const save = useCallback(
    async (params: SaveVaultMetadataParams): Promise<VaultMetadata | null> => {
      setLoading()
      try {
        const { data, error: dbError } = await db.insert<VaultMetadata>(
          'vault_metadata',
          {
            vault_id: params.vaultId,
            benefactor_address: params.benefactorAddress,
            beneficiary_address: params.beneficiaryAddress,
            beneficiary_email: params.beneficiaryEmail,
            beneficiary_phone: params.beneficiaryPhone ?? null,
            vault_type: params.vaultType,
            vault_name: params.vaultName ?? null,
            original_filename: params.originalFilename ?? null,
          },
        )
        if (dbError) {
          setError(dbError.message)
          return null
        }
        setDone()
        return data?.[0] ?? null
      } catch (e) {
        setError(
          e instanceof Error ? e.message : 'Failed to save vault metadata',
        )
        return null
      }
    },
    [],
  )

  const fetchByVaultId = useCallback(
    async (vaultId: number): Promise<VaultMetadata | null> => {
      setLoading()
      try {
        const result = await db.findOne<VaultMetadata>('vault_metadata', {
          filters: [{ column: 'vault_id', operator: 'eq', value: vaultId }],
        })
        const { data, error: dbError } = result
        console.log('[useVaultMetadata] fetchByVaultId raw response:', {
          vaultId,
          data,
          error: dbError,
        })
        if (dbError) {
          setError(dbError.message)
          return null
        }
        setDone()
        return data
      } catch (e) {
        console.error('[useVaultMetadata] fetchByVaultId error:', e)
        setError(
          e instanceof Error ? e.message : 'Failed to fetch vault metadata',
        )
        return null
      }
    },
    [],
  )

  const fetchByBenefactor = useCallback(
    async (address: string): Promise<VaultMetadata[]> => {
      setLoading()
      try {
        const { data, error: dbError } = await db.findMany<VaultMetadata>(
          'vault_metadata',
          {
            filters: [
              { column: 'benefactor_address', operator: 'eq', value: address },
            ],
            orderBy: { column: 'created_at', ascending: false },
          },
        )
        if (dbError) {
          setError(dbError.message)
          return []
        }
        setDone()
        return data ?? []
      } catch (e) {
        setError(
          e instanceof Error ? e.message : 'Failed to fetch vault metadata',
        )
        return []
      }
    },
    [],
  )

  return { loading, error, save, fetchByVaultId, fetchByBenefactor }
}
